import CaretDown16 from '@carbon/icons-react/es/caret--down/16';
import CaretUp16 from '@carbon/icons-react/es/caret--up/16';
import Search16 from '@carbon/icons-react/es/search/16';
import { difference, isEmpty, uniq } from 'lodash';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { MappingMechanism, ProjectMappingMechanism } from 'api/integrations';
import { Field } from 'api/tasks';
import { Collapse, Panel } from 'components/atoms/collapse';
import { Form } from 'components/atoms/form';
import { Input } from 'components/atoms/input';
import { computeSectionFieldMapping } from 'components/organisms/field-sections/utils';
import { FieldDataType } from 'components/organisms/module-settings/fields/types';
import { FormItem } from 'components/organisms/projects/project-create/common.styles';
import {
  fixedProjectFields,
  fixedFieldsRenderer,
  FixedFields,
} from 'components/organisms/projects/project-create/fixed-fields-render';
import { addDynamicFieldMarkerInLabel } from 'components/organisms/projects/project-create/get-native-fields-label';
import { getFiltredFieldIds } from 'components/organisms/projects/project-create/utils';
import {
  UserTypeFields,
  filterableFieldType,
} from 'components/organisms/tasks/fields';
import { Caption01, ProductiveHeading07 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { FieldHelper } from 'utils/field';
import { isSelectTypeField } from 'utils/project-fields/use-project-fields';
import { Col, Row } from '../styled';
import { BudgetedHoursField } from './budgeted-hours';
import {
  SectionContainer as CommonContainer,
  Label,
} from './project-fields-step.styles';
import { ProjectFieldsStepProps } from './project-fields-step.types';
import { getField, isMultiSelect, getSelectType } from './utils';

const CollapseSection = styled(Collapse)`
  .ant-collapse-header {
    ${ProductiveHeading07};
    color: var(--black-100);
    padding: 0 var(--spacing-05) !important;
    border-top: 1px solid var(--border-color) !important;
  }
  .ant-collapse-item.ant-collapse-item-active {
    padding-bottom: 0px;
  }
`;
const EmptyMessage = styled.div`
  ${Caption01};
  color: var(--gray-50);
  text-align: center;
  padding: var(--spacing-04) 0;
`;
const SectionContainer = styled(CommonContainer)`
  ${Row} {
    .ant-form-item-label > label {
      margin-left: var(--spacing-05);
    }
  }
`;
const SearchCol = styled(Col)`
  padding-bottom: var(--spacing-08);
`;
const OtherFieldsKey = 'other_fields';
const noneOfFieldsMatching = (fieldsIds: string[], targetIds: string[]) =>
  difference(fieldsIds, targetIds).length === uniq(fieldsIds).length;

export const ProjectFieldsStep: FC<ProjectFieldsStepProps> = ({
  templates,
  form,
  fields,
  jiraConfig,
  fieldFilters,
  fieldSections = [],
}) => {
  const searchPlaceholder = useTranslation(
    'project.overview.search_placeholder',
  );

  const [searchText, setSearchText] = useState<string>('');

  const getCustomFieldLabel = (field: Field) => {
    return fieldFilters?.customFields?.find(
      ({ fieldId }) => fieldId === field.fieldId,
    )
      ? addDynamicFieldMarkerInLabel(field.fieldLabel)
      : field.fieldLabel;
  };

  const fieldSectionMapping = useMemo(() => {
    return computeSectionFieldMapping(fields, fieldSections);
  }, [fields, fieldSections]);

  const filteredFieldIds = useMemo(
    () => getFiltredFieldIds(searchText, fields),
    [searchText, fields],
  );
  const budgetedHoursField = useMemo(
    () =>
      fixedProjectFields.find((x) => x.fieldLabel === FixedFields.BudgetHours),
    [],
  );
  const showJiraMapping =
    jiraConfig != null &&
    jiraConfig.settingsMeta.projectMappingMechanism ===
      ProjectMappingMechanism.Fixed &&
    jiraConfig.settingsMeta.epicMappingMechanism ===
      MappingMechanism.PromptDuringProjectcreation;

  const renderFixedField = fixedFieldsRenderer({
    filteredFieldIds,
    fieldFilters,
    jiraConfig,
  });

  return (
    <Form
      form={form}
      layout="horizontal"
      name="projectFieldsStepForm"
      labelCol={{ span: 7 }}
      wrapperCol={{ span: 17 }}
    >
      <Row justify="start">
        <SearchCol span={14}>
          <Input
            autoFocus
            value={searchText}
            placeholder={searchPlaceholder}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            suffix={isEmpty(searchText) ? <Search16 /> : undefined}
          />
        </SearchCol>
        {budgetedHoursField != null &&
        filteredFieldIds.includes(budgetedHoursField.fieldId.toString()) ? (
          <Col span={14}>
            <BudgetedHoursField templates={templates} form={form} />
          </Col>
        ) : null}
        <Col span={14}>
          <CollapseSection
            expandIconPosition="right"
            expandIcon={({ isActive }) => {
              return isActive ? <CaretDown16 /> : <CaretUp16 />;
            }}
            defaultActiveKey={fieldSectionMapping
              .map(({ fieldSectionId }) => fieldSectionId.toString())
              .concat(OtherFieldsKey)}
          >
            {fieldSectionMapping.map((fieldSection) => {
              return (
                <Panel
                  header={fieldSection.fieldSectionName}
                  key={fieldSection.fieldSectionId.toString()}
                >
                  <SectionContainer>
                    {noneOfFieldsMatching(
                      uniq(
                        fieldSection.fieldOrder.map(({ fieldId }) =>
                          fieldId.toString(),
                        ),
                      ),
                      filteredFieldIds,
                    ) && fieldSection.fieldOrder.length > 0 ? (
                      <EmptyMessage>
                        <FormattedMessage id="project.create.fields.section.empty" />
                      </EmptyMessage>
                    ) : null}
                    {fieldSection.fieldOrder.length
                      ? uniq(fieldSection.fieldOrder)
                          .map((field): [Field, any] => [
                            field,
                            getField(field),
                          ])
                          .map(([field, FieldComponent]) => (
                            <Row>
                              <Col key={field.fieldId} span={24}>
                                <FormItem
                                  name={[
                                    'fields',
                                    [
                                      FixedFields.ARR,
                                      FixedFields.ProjectFee,
                                    ].includes(field.fieldName)
                                      ? `_${field.fieldName}`
                                      : field.fieldId.toString(),
                                  ]}
                                  label={
                                    <Label
                                      ellipsis={{ tooltip: true }}
                                      $hasIcon={!isEmpty(field.description)}
                                    >
                                      {getCustomFieldLabel(field)}
                                    </Label>
                                  }
                                  tooltip={field.description}
                                  valuePropName={
                                    field.fieldDataType ===
                                    FieldDataType.Boolean
                                      ? 'checked'
                                      : undefined
                                  }
                                  initialValue={
                                    field.fieldDataType ===
                                    FieldDataType.Boolean
                                      ? false
                                      : undefined
                                  }
                                  hidden={
                                    !filteredFieldIds.includes(
                                      field.fieldId.toString(),
                                    )
                                  }
                                >
                                  {isSelectTypeField(field) ? (
                                    <FieldComponent
                                      isClearable
                                      options={
                                        UserTypeFields.includes(
                                          filterableFieldType(field),
                                        )
                                          ? FieldHelper(field).options
                                              .allOptions
                                          : FieldHelper(field).options
                                              .activeOptions
                                      }
                                      isMulti={isMultiSelect(field)}
                                      type={getSelectType(
                                        field.metaData?.options,
                                      )}
                                    />
                                  ) : (
                                    <FieldComponent />
                                  )}
                                </FormItem>
                              </Col>
                            </Row>
                          ))
                      : '-'}
                  </SectionContainer>
                </Panel>
              );
            })}
            {showJiraMapping ? (
              <Panel
                header={<FormattedMessage id="other_fields" />}
                key={OtherFieldsKey}
              >
                <SectionContainer>
                  {fixedProjectFields.map((fixedField) => (
                    <Row>
                      <Col span={24} key={fixedField.fieldId}>
                        {renderFixedField(fixedField)}
                      </Col>
                    </Row>
                  ))}
                </SectionContainer>
              </Panel>
            ) : null}
          </CollapseSection>
        </Col>
      </Row>
    </Form>
  );
};
